import { Flex } from '@fastnorth/kraken/components/layout';
import { forwardRef, type JSX, useEffect, useMemo, useRef } from 'react';
import { useParticipantDetails } from '../../../hooks/ParticipantDetailsProvider.tsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@fastnorth/kraken/components/button';
import { Download } from 'lucide-react';
import ShareButton from '../../../../../../../layout/components/ShareButton.tsx';
import useMergeTagSubstitutions from '../hooks/useMergeTagSubstitutions.ts';
import normalizeImageUrls from '../../../../../../../utils/normalizeImageUrls.ts';
import { Text } from '@fastnorth/kraken/components/typography';
import type { snapdom } from '@zumer/snapdom';
import type { EventTemplateContent } from '@fastnorth/timer-api';
import useAppLocation from '../../../../../../../hooks/useAppLocation.ts';
import {
  CERTIFICATE_PREVIEW_READY,
  TEMPLATE_CERTIFICATE_HEIGHT,
  TEMPLATE_CERTIFICATE_WIDTH,
} from '../consts.ts';
import CertificateScalingWrapper from './certificate-scaling-wrapper/CertificateScalingWrapper.tsx';
import CertificateContent from './certificate-content/CertificateContent.tsx';
import useCertificateDownload from '../hooks/useCertificateDownload.ts';

const FinisherCertificate = forwardRef<HTMLDivElement, Record<string, unknown>>(
  (_, ref): JSX.Element | null => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'karwa.components.participant_details.finisher_certificate',
    });

    const [{ resultDetail }] = useParticipantDetails();
    const { isCertificatePreview } = useAppLocation();
    const downloadRef = useRef<HTMLDivElement | null>(null);

    const certificateTemplate = useMemo(
      () => (resultDetail?.finisherCertificateTemplate || {}) as EventTemplateContent,
      [resultDetail?.finisherCertificateTemplate]
    );
    const { htmlContent, isLocked } = certificateTemplate;

    const derivedContent = useMergeTagSubstitutions({ content: htmlContent || '' });
    const substitutedContent = useMemo(() => normalizeImageUrls(derivedContent), [derivedContent]);
    const filename = resultDetail?.finisherCertificateFilename ?? 'finisher-certificate.png';
    const captureOptions = useMemo(
      () =>
        ({
          embedFonts: true,
          cache: 'full',
          dpr: 1,
          scale: 0.5,
        }) as Parameters<typeof snapdom>[1],
      []
    );
    const { downloadCertificate, isDownloading, isReady } = useCertificateDownload({
      captureOptions,
      captureRef: downloadRef,
      filename,
      readinessKey: substitutedContent,
      // Ported certificates carry the alignment they were authored with, so centring
      // every image would override it.
      shouldCenterImages: !isLocked,
    });

    useEffect(() => {
      if (!isCertificatePreview || !isReady || window.parent === window) {
        return;
      }

      let targetOrigin: string | null;
      try {
        targetOrigin = document.referrer ? new URL(document.referrer).origin : null;
      } catch {
        targetOrigin = null;
      }

      if (targetOrigin) {
        window.parent.postMessage({ type: CERTIFICATE_PREVIEW_READY }, targetOrigin);
      }
    }, [isCertificatePreview, isReady]);

    return (
      <Flex
        ref={ref}
        className={isCertificatePreview ? 'flex-col w-full' : 'flex-col w-full gap-4'}>
        <Flex
          data-testid={'download-certificate'}
          aria-hidden
          inert
          className={'flex-col overflow-hidden w-[3300px] min-h-[2550px]'}
          style={{ position: 'fixed', left: '-9999px', top: 0 }}
          ref={downloadRef}>
          <CertificateContent content={substitutedContent} isLocked={isLocked} />
        </Flex>
        <Flex
          spacing={isCertificatePreview ? '0' : '5 0'}
          className={'w-full drop-shadow-[2px_2px_6px_rgba(0,0,0,0.1)]'}>
          <CertificateScalingWrapper
            height={TEMPLATE_CERTIFICATE_HEIGHT}
            width={TEMPLATE_CERTIFICATE_WIDTH}>
            <CertificateContent
              content={substitutedContent}
              isLocked={isLocked}
              testId={'certificate-body-container'}
            />
          </CertificateScalingWrapper>
        </Flex>
        {!isCertificatePreview && (
          <Flex gap={'5'} className="lg:ml-6 lg:self-end">
            <Button
              disabled={!isReady || isDownloading}
              size="default"
              variant="outline"
              onClick={() => void downloadCertificate()}>
              <>
                <Download width={16} height={16} className={'mr-3'} />
                <Text>{t('download_text')}</Text>
              </>
            </Button>
            <ShareButton trackingLocation={'finisher_cert'} />
          </Flex>
        )}
      </Flex>
    );
  }
);

FinisherCertificate.displayName = 'FinisherCertificate';

export default FinisherCertificate;
